/**
 * Utility for loading Google Maps API script
 * Separated from context to follow Single Responsibility Principle
 */

// Check if Google Maps API is already loaded
export const isGoogleMapsLoaded = (): boolean => {
  return !!(window.google?.maps?.places && window.google?.maps?.Map);
};

// Check if script already exists in DOM
export const hasGoogleMapsScript = (): boolean => {
  return !!document.querySelector('script[src*="maps.googleapis.com"]');
};

// Create and load Google Maps script
export const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (isGoogleMapsLoaded()) {
        resolve();
      } else {
        reject(
          new Error('Google Maps API loaded but not properly initialized')
        );
      }
    };

    script.onerror = () => {
      reject(new Error('Failed to load Google Maps API'));
    };

    document.head.appendChild(script);
  });
};

// Wait for Google Maps to be loaded (when script exists but not yet loaded)
export const waitForGoogleMapsLoad = (): Promise<void> => {
  return new Promise(resolve => {
    const checkLoaded = () => {
      if (isGoogleMapsLoaded()) {
        resolve();
      } else {
        setTimeout(checkLoaded, 100);
      }
    };
    checkLoaded();
  });
};
